import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { TemperMark } from "../components/Logo";
import Marquee from "../components/Marquee";
import Testimonials from "../components/Testimonials";
import Seo from "../components/Seo";
import { MaskedLines, FadeUp, ParallaxImage } from "../components/Reveal";

const SPARKS =
  "https://images.unsplash.com/photo-1556608053-bc7b64163ba6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxnbG93aW5nJTIwYW1iZXIlMjBmaXJlJTIwc3BhcmtzJTIwYWJzdHJhY3R8ZW58MHx8fHwxNzkxNDgwOTU1fDA&ixlib=rb-4.1.0&q=85";
const COACHING =
  "https://images.unsplash.com/photo-1544486927-c903fcc7dcc3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxvbmUlMjBvbiUyMG9uZSUyMGNvYWNoaW5nJTIwY29udmVyc2F0aW9uJTIwdHdvJTIwbWVuJTIwdGFsa2luZyUyMG1pbmltYWx8ZW58MHx8fHwxNzkxNDgxNjA3fDA&ixlib=rb-4.1.0&q=85";
const TEAM =
  "https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMHRlYW0lMjBjb2xsYWJvcmF0aW9uJTIwbW9kZXJuJTIwb2ZmaWNlfGVufDB8fHx8MTc5MTQ4MDk1NXww&ixlib=rb-4.1.0&q=85";
const ARCH =
  "https://images.unsplash.com/photo-1738844153732-a485f0e78382?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMG1pbmltYWwlMjBhcmNoaXRlY3R1cmUlMjBzaGFkb3d8ZW58MHx8fHwxNzkxNDgwOTU1fDA&ixlib=rb-4.1.0&q=85";

const SERVICES = [
  {
    n: "01",
    title: "One-on-One Coaching",
    desc: "Private, candid coaching for leaders who carry real weight — built around your actual pressures, not a curriculum.",
    img: COACHING,
    span: "md:col-span-7",
  },
  {
    n: "02",
    title: "Team Building Clinics",
    desc: "Facilitated rooms where teams practice trust, candor, and repair — on real work, not trust falls.",
    img: TEAM,
    span: "md:col-span-5",
  },
  {
    n: "03",
    title: "Organizational Consulting",
    desc: "Clear-eyed diagnosis and counsel for organizations that need leadership to work at every level — not just the top.",
    img: ARCH,
    span: "md:col-span-12",
  },
];

const LETTERS = [
  { k: "T", w: "Truth", d: "You hear hard things early." },
  { k: "E", w: "Empathy", d: "People feel seen, not managed." },
  { k: "M", w: "Mastery", d: "Your standard raises theirs." },
  { k: "P", w: "Purpose", d: "The why stays loud." },
  { k: "E", w: "Endurance", d: "Pressure refines you." },
  { k: "R", w: "Resolve", d: "Hard calls get made." },
];

const MARQUEE = ["Discipline", "Warmth", "Control", "Growth", "Clarity", "Grit"];

export default function Home() {
  return (
    <>
      <Seo
        title="Leadership Development and Coaching | TEMPER Leadership"
        description="Executive coaching, corporate team building clinics, and organizational development consulting that forges leaders who hold steady under pressure."
        path="/"
      />
      <section data-testid="hero" className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden pt-28 md:pt-36">
        <div className="mx-auto w-full max-w-[1440px] px-6 pb-12 md:px-10">
          <div className="grid items-end gap-12 md:grid-cols-12">
            <div className="md:col-span-7">
              <motion.p
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="eyebrow flex items-center gap-3 text-ink/60"
              >
                <TemperMark className="h-3 w-3 text-ember" />
                Leadership Development — Coaching · Clinics · Consulting
              </motion.p>
              <MaskedLines
                as="h1"
                mode="load"
                delay={0.2}
                className="mt-8 font-display text-[clamp(2.8rem,7vw,6.75rem)] font-black leading-[0.95] tracking-[-0.03em]"
                lines={[
                  <span key="a">Great leaders</span>,
                  <span key="b">aren't born.</span>,
                  <span key="c">
                    They're <span className="text-ember">tempered.</span>
                  </span>,
                ]}
              />
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="mt-8 max-w-md text-lg leading-relaxed text-steel"
              >
                TEMPER develops leaders the way carbon becomes steel — honest heat, steady
                hands. Discipline plus warmth, built to hold under pressure.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.85, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="mt-10 flex flex-wrap gap-4"
              >
                <Link to="/temper-profile" data-testid="hero-primary-cta" className="btn-ember">
                  Take the TEMPER Profile <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/services" data-testid="hero-secondary-cta" className="btn-outline">
                  Explore the work
                </Link>
              </motion.div>
            </div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.55, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="hidden md:col-span-5 md:block"
            >
              <ParallaxImage
                src={SPARKS}
                alt="Sparks of a forge — the heat where leaders are tempered"
                className="h-[58vh] w-full"
                caption="Fig. 01 — Heat is only the beginning"
                loading="eager"
              />
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-16 flex items-center gap-4"
          >
            <span className="scroll-cue block h-10 w-px bg-ink/50" />
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink/50">
              Scroll
            </span>
          </motion.div>
        </div>
      </section>

      <Marquee items={MARQUEE} />

      <section data-testid="manifesto" className="py-24 md:py-40">
        <div className="mx-auto grid max-w-[1440px] gap-10 px-6 md:grid-cols-12 md:px-10">
          <div className="md:col-span-3">
            <FadeUp>
              <p className="eyebrow text-ember">01 — The work</p>
            </FadeUp>
          </div>
          <div className="md:col-span-9">
            <MaskedLines
              className="font-display text-3xl font-bold leading-[1.1] tracking-tight md:text-5xl"
              lines={[
                "Most training teaches technique.",
                "We temper character — because",
                "when the heat is on, that's what",
                <span key="x">
                  <span className="text-ember">holds.</span>
                </span>,
              ]}
            />
            <FadeUp className="mt-12 grid max-w-3xl gap-8 md:grid-cols-2">
              <p className="leading-relaxed text-steel">
                Anyone can memorize a framework. Under pressure, what carries a team is what
                can't be memorized: candor, calm, and care for the people behind the work.
                TEMPER exists to build exactly that — leadership development and coaching
                for people at every level, from the frontline to the top.
              </p>
              <p className="leading-relaxed text-steel">
                The name is the method. Steel is tempered — heated, worked, cooled — so
                strength and flexibility live in the same blade. Leaders are made the same
                way.
              </p>
            </FadeUp>
            <FadeUp className="mt-10">
              <Link to="/about" className="arrow-link group text-ink hover:text-ember">
                Meet Eric
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      <section data-testid="home-services" className="border-t border-line py-24 md:py-32">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <div className="flex items-end justify-between">
            <FadeUp>
              <p className="eyebrow text-ember">02 — Services</p>
              <h2 className="mt-6 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                Three ways in.
              </h2>
            </FadeUp>
            <FadeUp className="hidden md:block">
              <Link to="/services" className="arrow-link group text-ink hover:text-ember">
                All services
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </FadeUp>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-12">
            {SERVICES.map((s) => (
              <FadeUp key={s.n} className={`h-full ${s.span}`}>
                {s.n === "03" ? (
                  <Link
                    to="/services"
                    data-testid={`home-service-card-${s.n}`}
                    className="group grid h-full overflow-hidden border border-line transition-colors duration-300 hover:border-ink/40 md:grid-cols-2"
                  >
                    <div className="flex flex-col p-8 md:p-12">
                      <span className="font-mono text-[11px] tracking-[0.2em] text-ember">{s.n}</span>
                      <h3 className="mt-4 font-display text-2xl font-extrabold tracking-tight md:text-3xl">
                        {s.title}
                      </h3>
                      <p className="mt-3 leading-relaxed text-steel">{s.desc}</p>
                      <span className="arrow-link mt-auto pt-8 text-ink group-hover:text-ember">
                        Learn more
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>
                    <div className="relative h-56 overflow-hidden md:h-full">
                      <img
                        src={s.img}
                        alt={s.title}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                      />
                    </div>
                  </Link>
                ) : (
                  <Link
                    to="/services"
                    data-testid={`home-service-card-${s.n}`}
                    className="group flex h-full flex-col border border-line transition-colors duration-300 hover:border-ink/40"
                  >
                    <div className="relative h-56 overflow-hidden">
                      <img
                        src={s.img}
                        alt={s.title}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                      />
                    </div>
                    <div className="flex flex-1 flex-col p-8 md:p-10">
                      <span className="font-mono text-[11px] tracking-[0.2em] text-ember">{s.n}</span>
                      <h3 className="mt-4 font-display text-2xl font-extrabold tracking-tight md:text-3xl">
                        {s.title}
                      </h3>
                      <p className="mt-3 leading-relaxed text-steel">{s.desc}</p>
                      <span className="arrow-link mt-auto pt-8 text-ink group-hover:text-ember">
                        Learn more
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                )}
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="temper-profile-band" className="bg-coal py-24 text-paper md:py-36">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <FadeUp>
            <p className="eyebrow text-ember">The TEMPER Profile</p>
          </FadeUp>
          <div className="mt-8 grid gap-10 md:grid-cols-12 md:items-end">
            <MaskedLines
              className="font-display text-4xl font-extrabold leading-[1.02] tracking-tight md:col-span-8 md:text-6xl"
              lines={["Six dimensions.", <span key="x">One <span className="text-ember">honest read.</span></span>]}
            />
            <FadeUp className="md:col-span-4">
              <p className="max-w-sm leading-relaxed text-paper/60">
                Twelve statements, about four minutes. Find out how you lead under pressure —
                and where your temper edge is.
              </p>
            </FadeUp>
          </div>
          <div className="mt-16 grid grid-cols-2 gap-px border border-paper/15 bg-paper/15 md:grid-cols-3 lg:grid-cols-6">
            {LETTERS.map((l, i) => (
              <FadeUp key={l.w + i} delay={i * 0.06} className="bg-coal p-6 md:p-8">
                <span className="font-display text-4xl font-black text-ember md:text-5xl">{l.k}</span>
                <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em]">{l.w}</p>
                <p className="mt-2 text-sm leading-relaxed text-paper/50">{l.d}</p>
              </FadeUp>
            ))}
          </div>
          <FadeUp className="mt-12">
            <Link to="/temper-profile" data-testid="home-profile-cta" className="btn-ember">
              Discover your profile <ArrowRight className="h-4 w-4" />
            </Link>
          </FadeUp>
        </div>
      </section>

      <section data-testid="home-quote" className="py-24 md:py-40">
        <div className="mx-auto max-w-4xl px-6 md:px-10">
          <FadeUp>
            <TemperMark className="h-8 w-8 text-ember" />
          </FadeUp>
          <MaskedLines
            className="mt-8 font-display text-3xl font-bold leading-[1.12] tracking-tight md:text-5xl"
            lines={[
              "Heat doesn't break the blade.",
              "It shows you where it was",
              "weak — and where it wasn't.",
            ]}
          />
          <FadeUp className="mt-8">
            <p className="eyebrow text-ink/50">— Eric Ligeikis, Founder</p>
          </FadeUp>
        </div>
      </section>

      <Testimonials />
    </>
  );
}
